<template>
  <div class="vehicle-mmr-container">
    <h5 v-if="showTitle" class="mb-3">Vehicle Details</h5>

    <!-- Navigation tabs for different sections -->
    <b-tabs content-class="mt-3" small>
      <!-- Basic Information Tab -->
      <b-tab title="Basic Info" active>
        <b-list-group>
          <b-list-group-item
            v-for="(item, index) in filteredBasicInfoFields"
            :key="index"
            class="d-flex justify-content-between align-items-center"
          >
            <span class="font-weight-bold">{{ item.label }}:</span>
            <span v-if="item.isCurrency">{{
              formatCurrency(vehicle[item.key])
            }}</span>
            <span v-else-if="item.isDate">{{
              formatDate(vehicle[item.key])
            }}</span>
            <span v-else-if="item.isNumber">{{
              vehicle[item.key] | formatNumber
            }}</span>
            <span v-else>{{ vehicle[item.key] }}</span>
          </b-list-group-item>
        </b-list-group>
      </b-tab>

      <!-- Current Value Tab -->
      <b-tab title="Current Value">
        <b-card-text>
          <h6 class="mb-3">
            Value as of
            {{ vehicle.today | formatDate }}
          </h6>
          <b-list-group>
            <b-list-group-item
              v-for="(item, index) in filteredCurrentValueFields"
              :key="index"
              class="d-flex justify-content-between align-items-center"
            >
              <span class="font-weight-bold">{{ item.label }}:</span>
              <span>{{ formatCurrency(vehicle[item.key]) }}</span>
            </b-list-group-item>
          </b-list-group>
        </b-card-text>
      </b-tab>

      <!-- Historical Value Tab -->
      <b-tab title="Historical">
        <b-card-text>
          <div v-if="hasHistoricalData">
            <h6 class="mb-3">
              Value 6 Months Ago
              {{
                vehicle.date6MonthsAgo
                  ? "(" + formatDate(vehicle.date6MonthsAgo) + ")"
                  : ""
              }}
            </h6>
            <b-list-group class="mb-3">
              <b-list-group-item
                v-for="(item, index) in filteredHistoricalFields6Months"
                :key="index"
                class="d-flex justify-content-between align-items-center"
              >
                <span class="font-weight-bold">{{ item.label }}:</span>
                <span>{{ formatCurrency(vehicle[item.key]) }}</span>
              </b-list-group-item>
            </b-list-group>

            <h6 class="mb-3">
              Value 12 Months Ago
              {{
                vehicle.date12MonthsAgo
                  ? "(" + formatDate(vehicle.date12MonthsAgo) + ")"
                  : ""
              }}
            </h6>
            <b-list-group>
              <b-list-group-item
                v-for="(item, index) in filteredHistoricalFields12Months"
                :key="index"
                class="d-flex justify-content-between align-items-center"
              >
                <span class="font-weight-bold">{{ item.label }}:</span>
                <span>{{ formatCurrency(vehicle[item.key]) }}</span>
              </b-list-group-item>
            </b-list-group>
          </div>
          <div v-else class="text-center py-3">
            No historical data available
          </div>
        </b-card-text>
      </b-tab>

      <!-- Forecast Tab -->
      <b-tab title="Forecast">
        <b-card-text>
          <div v-if="hasForecastData">
            <b-tabs pills vertical>
              <b-tab
                v-for="(period, periodIndex) in forecastPeriods"
                :key="periodIndex"
                :title="
                  period.label +
                  (vehicle[period.dateKey]
                    ? ' (' + formatDate(vehicle[period.dateKey]) + ')'
                    : '')
                "
              >
                <b-list-group>
                  <b-list-group-item
                    v-for="(valueType, valueIndex) in getFilteredForecastValues(
                      period
                    )"
                    :key="valueIndex"
                    class="d-flex justify-content-between align-items-center"
                  >
                    <span class="font-weight-bold">{{ valueType.label }}:</span>
                    <span>{{
                      formatCurrency(vehicle[period.prefix + valueType.suffix])
                    }}</span>
                  </b-list-group-item>
                </b-list-group>
              </b-tab>
            </b-tabs>
          </div>
          <div v-else class="text-center py-3">No forecast data available</div>
        </b-card-text>
      </b-tab>

      <!-- Additional Info Tab -->
      <b-tab title="Additional Info">
        <b-card-text>
          <b-list-group>
            <b-list-group-item
              v-for="(item, index) in filteredAdditionalInfoFields"
              :key="index"
              class="d-flex justify-content-between align-items-center"
            >
              <span class="font-weight-bold">{{ item.label }}:</span>
              <span v-if="item.isCurrency">{{
                formatCurrency(vehicle[item.key])
              }}</span>
              <span v-else-if="item.isDate">{{
                formatDate(vehicle[item.key])
              }}</span>
              <span v-else>{{ vehicle[item.key] }}</span>
            </b-list-group-item>
          </b-list-group>
        </b-card-text>
      </b-tab>
    </b-tabs>
  </div>
</template>
<script>
export default {
  name: "VehicleMMR",
  props: {
    vehicle: {
      type: Object,
      required: true,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // Define field configurations for each section
      basicInfoFields: [
        { key: "vinNumber", label: "VIN" },
        { key: "year", label: "Year" },
        { key: "make", label: "Make" },
        { key: "model", label: "Model" },
        { key: "trim", label: "Trim" },
        {
          key: "mileage",
          label: "Mileage",
          isNumber: true,
        },
        { key: "style", label: "Style" },
        { key: "color", label: "Color" },
        { key: "region", label: "Region" },
        { key: "assetCategory", label: "Asset Category" },
        { key: "acquisitionCost", label: "Acquisition Cost", isCurrency: true },
        { key: "acquisitionDate", label: "Acquisition Date", isDate: true },
      ],
      currentValueFields: [
        { key: "costTodayAbove", label: "Above Average" },
        { key: "costTodayAvg", label: "Average" },
        { key: "costTodayBelow", label: "Below Average" },
      ],
      historicalFields6Months: [
        { key: "cost6MonthsAgoAbove", label: "Above Average" },
        { key: "cost6MonthsAgoAvg", label: "Average" },
        { key: "cost6MonthsAgoBelow", label: "Below Average" },
      ],
      historicalFields12Months: [
        { key: "cost12MonthsAgoAbove", label: "Above Average" },
        { key: "cost12MonthsAgoAvg", label: "Average" },
        { key: "cost12MonthsAgoBelow", label: "Below Average" },
      ],
      forecastPeriods: [
        {
          label: "3 Months",
          prefix: "costForecasted3Months",
          dateKey: "forecasted3Months",
        },
        {
          label: "6 Months",
          prefix: "costForecasted6Months",
          dateKey: "forecasted6Months",
        },
        {
          label: "9 Months",
          prefix: "costForecasted9Months",
          dateKey: "forecasted9Months",
        },
        {
          label: "12 Months",
          prefix: "costForecasted12Months",
          dateKey: "forecasted12Months",
        },
      ],
      forecastValueTypes: [
        { label: "Above Average", suffix: "Above" },
        { label: "Average", suffix: "Avg" },
        { label: "Below Average", suffix: "Below" },
      ],
      additionalInfoFields: [
        { key: "transmission", label: "Transmission" },
        { key: "mileage", label: "Mileage" },
        { key: "engine", label: "Engine" },
        { key: "drivetrain", label: "Drivetrain" },
        { key: "fuelType", label: "Fuel Type" },
        { key: "msrp", label: "MSRP", isCurrency: true },
        { key: "basePrice", label: "Base Price", isCurrency: true },
        { key: "adjustedPrice", label: "Adjusted Price", isCurrency: true },
        { key: "marketValue", label: "Market Value", isCurrency: true },
        { key: "exteriorColor", label: "Exterior Color" },
        { key: "interiorColor", label: "Interior Color" },
        { key: "condition", label: "Condition" },
        { key: "origin", label: "Origin" },
        { key: "supplier", label: "Supplier" },
        { key: "status", label: "Status" },
        { key: "createdAt", label: "Created At", isDate: true },
      ],
    };
  },
  computed: {
    filteredBasicInfoFields() {
      if (!this.vehicle) {
        return [];
      }

      return this.basicInfoFields.filter(
        (item) =>
          this.vehicle[item.key] !== undefined &&
          this.vehicle[item.key] !== null &&
          this.vehicle[item.key] !== ""
      );
    },
    filteredCurrentValueFields() {
      if (!this.vehicle) {
        return [];
      }

      return this.currentValueFields.filter(
        (item) =>
          this.vehicle[item.key] !== undefined &&
          this.vehicle[item.key] !== null &&
          this.vehicle[item.key] !== ""
      );
    },
    filteredHistoricalFields6Months() {
      if (!this.vehicle) {
        return [];
      }

      return this.historicalFields6Months.filter(
        (item) =>
          this.vehicle[item.key] !== undefined &&
          this.vehicle[item.key] !== null &&
          this.vehicle[item.key] !== ""
      );
    },
    filteredHistoricalFields12Months() {
      if (!this.vehicle) {
        return [];
      }

      return this.historicalFields12Months.filter(
        (item) =>
          this.vehicle[item.key] !== undefined &&
          this.vehicle[item.key] !== null &&
          this.vehicle[item.key] !== ""
      );
    },
    filteredAdditionalInfoFields() {
      if (!this.vehicle) {
        return [];
      }

      return this.additionalInfoFields.filter(
        (item) =>
          this.vehicle[item.key] !== undefined &&
          this.vehicle[item.key] !== null &&
          this.vehicle[item.key] !== ""
      );
    },
    hasHistoricalData() {
      if (!this.vehicle) {
        return false;
      }

      // Check if any historical data exists
      return (
        this.filteredHistoricalFields6Months.length > 0 ||
        this.filteredHistoricalFields12Months.length > 0
      );
    },
    hasForecastData() {
      if (!this.vehicle) {
        return false;
      }

      // Check if any forecast data exists
      return this.forecastPeriods.some((period) =>
        this.forecastValueTypes.some((valueType) => {
          const key = period.prefix + valueType.suffix;
          return (
            this.vehicle[key] !== undefined &&
            this.vehicle[key] !== null &&
            this.vehicle[key] !== ""
          );
        })
      );
    },
  },
  methods: {
    formatCurrency(value) {
      if (value === undefined || value === null || value === "") return "";

      // Handle the case when value is a string
      const numericValue =
        typeof value === "string"
          ? parseFloat(value.replace(/[^0-9.-]+/g, ""))
          : value;

      // Check if conversion resulted in a valid number
      if (isNaN(numericValue)) return value;

      // Format as currency with the appropriate locale and currency symbol
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(numericValue);
    },
    formatDate(dateString) {
      if (dateString === undefined || dateString === null || dateString === "")
        return "";

      try {
        // Fix for timezone issue causing dates to display one day earlier
        // Parse the date parts directly to avoid timezone conversions
        if (
          typeof dateString === "string" &&
          dateString.match(/^\d{4}-\d{2}-\d{2}/)
        ) {
          // For ISO format dates (YYYY-MM-DD...)
          const [year, month, day] = dateString
            .split("T")[0]
            .split("-")
            .map(Number);
          // Create date with explicit year, month (0-based), and day parameters
          const date = new Date(year, month - 1, day);

          return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }).format(date);
        } else {
          // For other date formats
          const date = new Date(dateString);

          // Check if date is valid before formatting
          if (isNaN(date.getTime())) {
            return dateString; // Return original string if parsing fails
          }

          return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }).format(date);
        }
      } catch (e) {
        console.error("Error formatting date:", e);
        return dateString; // Return original string if parsing fails
      }
    },
    getFilteredForecastValues(period) {
      if (!period || !this.vehicle) {
        return [];
      }

      return this.forecastValueTypes.filter((valueType) => {
        const key = period.prefix + valueType.suffix;
        return (
          this.vehicle[key] !== undefined &&
          this.vehicle[key] !== null &&
          this.vehicle[key] !== ""
        );
      });
    },
  },
};
</script>
<style scoped>
.vehicle-mmr-container {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.list-group-item {
  border-left: none;
  border-right: none;
  padding: 0.5rem 1rem;
  font-size: 0.85rem; /* Smaller font size to match compact tables */
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:last-child {
  border-bottom: none;
}

.font-weight-bold {
  color: #495057;
  font-size: 0.85rem; /* Match the compact styling */
}

/* Tab styling */
::v-deep .nav-tabs {
  border-bottom: 1px solid #dee2e6;
  font-size: 0.9rem;
}

::v-deep .nav-tabs .nav-link {
  color: #6c757d;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

::v-deep .nav-tabs .nav-link.active {
  color: #007bff;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}

/* Vertical pills styling for forecast tab */
::v-deep .nav-pills.flex-column {
  max-width: 250px;
  min-width: 150px;
  border-right: 1px solid #e9ecef;
}

::v-deep .nav-pills.flex-column .nav-link {
  border-radius: 0;
  text-align: left;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
}

::v-deep .nav-pills.flex-column .nav-link.active {
  background-color: #f8f9fa;
  color: #007bff;
  border-left: 3px solid #007bff;
}

/* Card text padding adjustment */
::v-deep .card-text {
  padding: 0;
}

/* Section heading style */
h6 {
  color: #6c757d;
  font-weight: 600;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.5rem;
}

/* Ensure the list items don't wrap awkwardly on small screens */
@media (max-width: 768px) {
  .list-group-item {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .list-group-item span:last-child {
    margin-top: 0.25rem;
  }

  /* Make vertical tabs horizontal on mobile */
  ::v-deep .nav-pills.flex-column {
    max-width: 100%;
    min-width: auto;
    display: flex;
    flex-direction: row !important;
    flex-wrap: wrap;
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    margin-bottom: 1rem;
  }

  ::v-deep .nav-pills.flex-column .nav-link {
    padding: 0.25rem 0.5rem;
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
  }

  ::v-deep .nav-pills.flex-column .nav-link.active {
    border-left: none;
    border-bottom: 2px solid #007bff;
  }
}
</style>
